export interface LineRandom {
    readonly k: ReadonlyArray<number>,
    readonly variance: ReadonlyArray<number>,
}

export interface RectRandom {
    readonly openCorners: ReadonlyArray<boolean>,
    readonly endPoints: ReadonlyArray<ReadonlyArray<number>>,
    readonly lines: ReadonlyArray<LineRandom>,
}


const KAPPA = 4 * (Math.sqrt(2)-1) / 3;

type CornerType = 'CLOSED' | 'RANDOM';

function createRandomArray(count: number, scale: number, add: number = 0): ReadonlyArray<number> {
    const result = [];
    for (let i = 0; i < count; i++) {
        result.push(scale * Math.random() + add);
    }
    return result;
}


export function createLineRandom(): LineRandom {
    if (Math.random() < 0.5) {
        const m1 = Math.random() * 28 / 100 + 14 / 100;
        const m2 = Math.random() * 28 / 100 + 58 / 100;
        return {
            k: [
                (Math.random() * 28 / 100 + 14 / 100) * m1,
                (Math.random() * 28 / 100 + 58 / 100) * m1,
                m1,
                (Math.random() * 28 / 100 + 58 / 100) * (m2 - m1) + m1,
                m2,
                (Math.random() * 28 / 100 + 58 / 100) * (1 - m2) + m2,
            ],
            variance: createRandomArray(6, 0.005)
        };
    } else {
        const m1 = Math.random() * 20 / 100 + 10 / 100;
        const m2 = Math.random() * 20 / 100 + 40 / 100;
        const m3 = Math.random() * 20 / 100 + 70 / 100;
        return {
            k: [
                (Math.random() * 28 / 100 + 14 / 100) * m1,
                (Math.random() * 28 / 100 + 58 / 100) * m1,
                m1,
                (Math.random() * 28 / 100 + 58 / 100) * (m2 - m1) + m1,
                m2,
                (Math.random() * 28 / 100 + 58 / 100) * (m3 - m2) + m2,
                m3,
                (Math.random() * 28 / 100 + 50 / 100) * (1 - m3) + m3,
            ],
            variance: createRandomArray(8, 0.005)
        };
    }
}


export function createSectionPath(x1: number, y1: number, x2: number, y2: number, random: LineRandom, shouldReflect: boolean = false) {
    const reflect = shouldReflect ? -1 : 1;
    const n = random.k.length;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.max(1, Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2)));
    const logL = Math.log(length);
    const r = Math.pow(logL, 4) / length;

    let cx0 = random.k[0] * dx + random.variance[0] * r * dy * reflect + x1;
    let cy0 = random.k[0] * dy - random.variance[0] * r * dx * reflect + y1;
    let cx1 = random.k[1] * dx + random.variance[1] * r * dy * reflect + x1;
    let cy1 = random.k[1] * dy - random.variance[1] * r * dx * reflect + y1;
    const cx2 = random.k[2] * dx + random.variance[2] * r * dy * reflect + x1;
    const cy2 = random.k[2] * dy - random.variance[2] * r * dx * reflect + y1;
    let result = ' C ' + cx0 + ' ' + cy0 + ' ' + cx1 + ' ' + cy1 + ' ' + cx2 + ' ' + cy2;

    for (let i = 3; i < n - 1; i += 2) {
        cx0 = random.k[i] * dx + random.variance[i] * r * dy * reflect + x1;
        cy0 = random.k[i] * dy - random.variance[i] * r * dx * reflect + y1;
        cx1 = random.k[i + 1] * dx + random.variance[i + 1] * r * dy * reflect + x1;
        cy1 = random.k[i + 1] * dy - random.variance[i + 1] * r * dx * reflect + y1;
        result += ' S ' + cx0 + ' ' + cy0 + ' ' + cx1 + ' ' + cy1;
    }

    cx0 = random.k[n - 1] * dx + random.variance[n - 1] * r * dy * reflect + x1;
    cy0 = random.k[n - 1] * dy - random.variance[n - 1] * r * dx * reflect + y1;
    result += ' S ' + cx0 + ' ' + cy0 + ' ' + x2 + ' ' + y2;
    return result;
}


export function createLinePath(x1: number, y1: number, x2: number, y2: number, random: LineRandom, shouldReflect: boolean = false) {
    const section = createSectionPath(x1, y1, x2, y2, random, shouldReflect);
    return `M ${x1} ${y1}${section}`;
}


export function createRectRandom(cornerType: CornerType = 'RANDOM'): RectRandom {
    const openCorners = [];
    const endPoints = [];
    const lines = [];
    for (let i = 0; i < 4; i++) {
        const openCorner = cornerType === 'CLOSED' ? false : Math.random() < 0.35;
        openCorners.push(openCorner);
        if (openCorner) {
            endPoints.push(createRandomArray(4, 0.02, -0.01));
        } else {
            const r = createRandomArray(4, 0.02, -0.01);
            const xMin = Math.min(r[0], r[1]);
            const xMax = Math.max(r[0], r[1]);
            const yMin = Math.min(r[2], r[3]);
            const yMax = Math.max(r[2], r[3]);
            if (i === 0 || i === 2) {
                endPoints.push([xMin, yMax, xMax, yMin]);
            } else {
                endPoints.push([xMax, yMin, xMin, yMax]);
            }
        }
        lines.push(createLineRandom());
    }
    return {
        openCorners,
        endPoints,
        lines,
    };
}


export function createRectPath(x1: number, y1: number, x2: number, y2: number, random: RectRandom): string {
    let result = '';

    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.max(1, Math.min(Math.abs(dx), Math.abs(dy)));
    const logL = Math.log(length);
    const r = Math.pow(logL, 4);

    let xe = x2 > x1 ? x1 + random.endPoints[0][0] * r : x1 - random.endPoints[0][0] * r;
    let ye = y2 > y1 ? y1 + random.endPoints[0][1] * r : y1 - random.endPoints[0][1] * r;

    const x = [x1, x2, x2, x1];
    const y = [y1, y1, y2, y2];

    if (!random.openCorners[0]) {
        result += ' M ' + xe + ' ' + ye;
    }

    for (let i = 0; i < 4; i++) {
        const xa = x[(i + 2) % 4] > x[i] ? x[i] + random.endPoints[i][2] * r : x[i] - random.endPoints[i][2] * r;
        const ya = y[(i + 2) % 4] > y[i] ? y[i] + random.endPoints[i][3] * r : y[i] - random.endPoints[i][3] * r;
        if (random.openCorners[i]) {
            result += ' M ' + xa + ' ' + ya;
        } else if (i === 0 || i === 2) {
            result += ' Q ' + xe + ' ' + ya + ' ' + xa + ' ' + ya;
        } else {
            result += ' Q ' + xa + ' ' + ye + ' ' + xa + ' ' + ya;
        }
        xe = x[(i + 3) % 4] > x[(i + 1) % 4] ?
            x[(i + 1) % 4] + random.endPoints[(i + 1) % 4][0] * r
            :
            x[(i + 1) % 4] - random.endPoints[(i + 1) % 4][0] * r;
        ye = y[(i + 3) % 4] > y[(i + 1) % 4] ?
            y[(i + 1) % 4] + random.endPoints[(i + 1) % 4][1] * r
            :
            y[(i + 1) % 4] - random.endPoints[(i + 1) % 4][1] * r;
        result += createSectionPath(xa, ya, xe, ye, random.lines[i], (x1 < x2) === (y1 < y2));
    }

    if (! random.openCorners.reduce((a, b) => a || b)) {
        result += ' Z';
    }

    return result;
}


export function createEllipseRandom(): ReadonlyArray<number> {
    return createRandomArray(26, 0.02, -0.01);
}

export function createEllipsePath (x1: number, y1: number, x2: number, y2: number, random: ReadonlyArray<number>): string {
    const yMin = Math.min(y1, y2);
    const yMax = Math.max(y1, y2);

    const xm = (x2 + x1) / 2;
    const xd = (x2 - x1) / 2;
    const xk = xd * KAPPA;
    const xm1 = xm - xk;
    const xm2 = xm + xk;
    const ym = (yMin + yMax) / 2;
    const yd = (yMin - yMax) / 2;
    const yk = yd * KAPPA;
    const ym1 = ym - yk;
    const ym2 = ym + yk;
    const p = [xm, yMin,
        xm1, yMin, x1, ym2, x1, ym,
        xm1, yMax, xm, yMax,
        x2, ym1, x2, ym,
        xm2, yMin, xm, yMin];
    const length = Math.max(1, 2 * Math.min(Math.abs(xd), Math.abs(yd)));
    const logL = Math.log(length)
    const r = Math.pow(logL, 4);

    for (let i = 0; i < 18; i += 2) {
        p[i  ] += (x1 < x2)? random[i  ] * r : -random[i  ] * r;
        p[i+1] += (y1 < y2)? random[i+1] * r : -random[i+1] * r;
    }

    p[18] += (x1 < x2)? 2 * random[18] * r : -2 * random[18] * r;
    p[19] += (y1 < y2)? 2 * random[19] * r : -2 * random[19] * r;

    let result = `M${p[0]} ${p[1]}C${p[2]} ${p[3]} ${p[4]} ${p[5]} ${p[6]} ${p[7]} `;

    for (let i = 8; i < 20; i += 4) {
        result += `S ${p[i]} ${p[i+1]} ${p[i+2]} ${p[i+3]} `;
    }

    return result;
}



// WEBPACK FOOTER //
// ./src/services/path-data.ts